import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { Repeat, CircleNotch, BellSimpleRinging, CheckCircle, Sparkle, TrendUp, CopySimple } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function SubscriptionWatchlist({ businessId, canEditBooks = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/businesses/${businessId}/subscriptions/watchlist`);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (sub, status) => {
    setBusyKey(sub.payee_key);
    try {
      const { data: res } = await api.post(`/businesses/${businessId}/subscriptions/watchlist/status`, { payee_key: sub.payee_key, status });
      toast.success(res?.rowan_message || "Saved.");
      await load();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setBusyKey("");
    }
  };

  const acknowledgeHike = async (sub) => {
    setBusyKey(sub.payee_key);
    try {
      const { data: res } = await api.post(`/businesses/${businessId}/subscriptions/hikes/acknowledge`, { payee_key: sub.payee_key, hike_date: sub.price_hike.date });
      toast.success(res?.rowan_message || "Noted.");
      await load();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setBusyKey("");
    }
  };

  const subs = data?.subscriptions || [];
  if (!loading && subs.length === 0) return null;

  return (
    <div className="bk-rowan-card p-6" data-testid="subscription-watchlist">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-xl font-bold flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
            <Repeat size={20} weight="duotone" color="#6B4BA3" /> Subscription watchlist
          </h3>
          <p className="text-sm mt-1 flex items-start gap-1.5" style={{ color: "var(--bk-text-2)" }} data-testid="subscription-rowan-message">
            <Sparkle size={14} weight="fill" color="var(--bk-gold)" className="shrink-0 mt-0.5" /> {data?.rowan_message || "Scanning your books for recurring charges…"}
          </p>
        </div>
        {data && (
          <div className="flex gap-2">
            <div className="rounded-2xl border px-4 py-2.5 bg-white/92 text-center" style={{ borderColor: "var(--bk-border)" }} data-testid="subscription-monthly-total">
              <p className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Monthly</p>
              <p className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>{money(data.monthly_total)}</p>
            </div>
            <div className="rounded-2xl border px-4 py-2.5 bg-white/92 text-center" style={{ borderColor: "var(--bk-border)" }} data-testid="subscription-annual-estimate">
              <p className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Yearly est.</p>
              <p className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>{money(data.annual_estimate)}</p>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-4 text-sm inline-flex items-center gap-2" style={{ color: "var(--bk-text-2)" }} data-testid="subscription-loading">
          <CircleNotch size={16} className="animate-spin" /> Checking for recurring charges…
        </div>
      ) : (
        <>
          {(data?.duplicates || []).length > 0 && (
            <div className="mt-4 space-y-2" data-testid="subscription-overlap-list">
              {data.duplicates.map((d) => (
                <div key={d.job} className="rounded-2xl border px-4 py-3 flex items-start gap-2.5 text-sm" style={{ borderColor: "rgba(107,75,163,0.3)", background: "#F0EBFA" }} data-testid={`subscription-overlap-${d.job.replace(/\s+/g, "-")}`}>
                  <CopySimple size={16} weight="bold" color="#6B4BA3" className="shrink-0 mt-0.5" />
                  <p style={{ color: "#4A3572" }}>
                    <b>Possible overlap:</b> {d.names.join(" + ")} both look like {d.job} — about <b>{money(d.combined_monthly)}/mo</b> combined. Sure you need both?
                  </p>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 space-y-2" data-testid="subscription-list">
          {subs.map((sub) => {
            const busy = busyKey === sub.payee_key;
            const isCancel = sub.status === "cancel";
            const isKeep = sub.status === "keep";
            return (
              <div
                key={sub.payee_key}
                className="rounded-[20px] border px-4 py-3 bg-white/92 flex flex-wrap items-center gap-3"
                style={{ borderColor: isCancel ? "rgba(217,108,74,0.4)" : "var(--bk-border)" }}
                data-testid={`subscription-row-${sub.payee_key.replace(/\s+/g, "-")}`}
              >
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm font-semibold flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
                    {sub.name}
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#F0EBFA", color: "#6B4BA3" }}>{sub.interval}</span>
                    {sub.price_hike && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={sub.price_hike.acknowledged ? { background: "#EEF0F2", color: "#6B7280" } : { background: "#FFF4D9", color: "#8A6A3B" }} data-testid={`subscription-hike-pill-${sub.payee_key.replace(/\s+/g, "-")}`}>
                        <TrendUp size={10} weight="bold" /> {money(sub.price_hike.previous)} → {money(sub.price_hike.current)} (+{sub.price_hike.pct}%){sub.price_hike.acknowledged ? " · accepted" : ""}
                      </span>
                    )}
                    {isCancel && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: "#FDECE8", color: "#B23B1F" }} data-testid={`subscription-cancel-pill-${sub.payee_key.replace(/\s+/g, "-")}`}><BellSimpleRinging size={10} weight="fill" /> Cancel reminder set</span>}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>
                    {money(sub.amount)} · {sub.occurrences}× charged · last {sub.last_charged} · {sub.category || "Uncategorized"} · {money(sub.total_paid)} total
                  </p>
                  {sub.price_hike && !sub.price_hike.acknowledged && (
                    <p className="text-xs mt-1 font-medium flex items-center gap-2" style={{ color: "#8A6A3B" }} data-testid={`subscription-hike-note-${sub.payee_key.replace(/\s+/g, "-")}`}>
                      Rowan caught this one charging more than last cycle — nobody asked you first.
                      <button
                        type="button"
                        onClick={() => acknowledgeHike(sub)}
                        disabled={!canEditBooks || busy}
                        data-testid={`subscription-hike-ack-${sub.payee_key.replace(/\s+/g, "-")}`}
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold border transition-colors hover:bg-black/5 disabled:opacity-60"
                        style={{ borderColor: "rgba(138,106,59,0.4)", color: "#8A6A3B" }}
                      >
                        Got it — accept new price
                      </button>
                    </p>
                  )}
                  {isCancel && (
                    <p className="text-xs mt-1 font-medium" style={{ color: "#B23B1F" }}>Rowan will keep nudging you here until this one stops charging you.</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Est. monthly</p>
                  <p className="font-display text-base font-bold" style={{ color: "var(--bk-text)" }}>{money(sub.monthly_cost)}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => setStatus(sub, isKeep ? "none" : "keep")}
                    disabled={!canEditBooks || busy}
                    data-testid={`subscription-keep-${sub.payee_key.replace(/\s+/g, "-")}`}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold border inline-flex items-center gap-1 disabled:opacity-60"
                    style={isKeep ? { background: "var(--bk-success)", borderColor: "var(--bk-success)", color: "#fff" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
                  >
                    <CheckCircle size={12} weight="fill" /> Keep
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatus(sub, isCancel ? "none" : "cancel")}
                    disabled={!canEditBooks || busy}
                    data-testid={`subscription-cancel-${sub.payee_key.replace(/\s+/g, "-")}`}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold border inline-flex items-center gap-1 disabled:opacity-60"
                    style={isCancel ? { background: "#D96C4A", borderColor: "#D96C4A", color: "#fff" } : { borderColor: "rgba(217,108,74,0.35)", color: "#B23B1F" }}
                  >
                    {busy ? <CircleNotch size={12} className="animate-spin" /> : <BellSimpleRinging size={12} weight="fill" />} Remind me to cancel
                  </button>
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}
    </div>
  );
}
